import React, { useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { Button } from '@trello/nachos/button';
import { DownIcon } from '@trello/nachos/icons/down';
import { Popover, usePopover } from '@trello/nachos/popover';
import { Analytics } from '@trello/atlassian-analytics';
import styles from './HeaderMenu.less';
import type { ActionSubjectIdType } from '@trello/atlassian-analytics';
import { useBoardBackground } from 'app/src/components/Theme';

interface HeaderMenuProps {
  buttonText: string;
  analyticsButtonName: ActionSubjectIdType;
  analyticsComponentName: ActionSubjectIdType;
  popoverTitle: string;
  dataTestId?: string;
  // when shouldHidePopover is true, invoke Nachos Popover's `hide` function
  shouldHidePopover?: boolean;
  // when popover visibility changes from visible to not visible,
  // `resetShouldHidePopover` is invoked if defined
  resetShouldHidePopover?: () => void;
  dangerous_classNamePopover?: string;
  noHorizontalPadding?: boolean;
  pushScreen?: { screen: number | undefined; name: string };
  onClick?: () => void;
}

export const HeaderMenu: React.FC<HeaderMenuProps> = ({
  buttonText,
  analyticsButtonName,
  analyticsComponentName,
  popoverTitle,
  dataTestId,
  shouldHidePopover,
  resetShouldHidePopover,
  children,
  dangerous_classNamePopover,
  noHorizontalPadding,
  pushScreen,
  onClick,
}) => {
  const {
    popoverProps,
    triggerRef,
    toggle,
    hide,
    push,
    pop,
  } = usePopover<HTMLButtonElement>();

  const { isVisible: popOverIsVisible } = popoverProps;
  const { backgroundBrightness } = useBoardBackground();

  const handleClick = useCallback(() => {
    onClick?.();
    toggle();
    Analytics.sendClickedButtonEvent({
      buttonName: analyticsButtonName,
      source: 'appHeader',
    });
  }, [onClick, toggle, analyticsButtonName]);

  const handleHide = useCallback(() => {
    hide();
    Analytics.sendClosedComponentEvent({
      componentType: 'inlineDialog',
      componentName: analyticsComponentName,
      source: 'appHeader',
    });
  }, [hide, analyticsComponentName]);

  const handleBack = useCallback(() => {
    if (popoverProps.onBack) {
      popoverProps.onBack();
    } else {
      pop();
    }
  }, [popoverProps, pop]);

  useEffect(() => {
    if (shouldHidePopover) {
      hide();
    }
  }, [shouldHidePopover, hide]);

  useEffect(() => {
    if (!popOverIsVisible) {
      resetShouldHidePopover && resetShouldHidePopover();
    }
  }, [popOverIsVisible, resetShouldHidePopover]);

  useEffect(() => {
    if (pushScreen?.screen) {
      push(pushScreen.screen);
    }
    // eslint-disable-next-line @trello/exhaustive-deps
  }, [push, pushScreen]);

  return (
    <>
      <Button
        appearance={
          backgroundBrightness === 'dark' ? 'transparent' : 'transparent-dark'
        }
        className={classNames(styles.button, {
          [styles.buttonOpen]: popOverIsVisible,
        })}
        data-test-id={dataTestId}
        title={buttonText}
        // set aria label so screen reader doesn't read out the down icon
        aria-label={popoverTitle}
        onClick={handleClick}
        ref={triggerRef}
        iconAfter={
          <DownIcon size="small" dangerous_className={styles.buttonIcon} />
        }
      >
        <span className={styles.buttonText}>{buttonText}</span>
      </Button>
      <Popover
        {...popoverProps}
        title={popoverTitle}
        onHide={handleHide}
        onBack={handleBack}
        dangerous_className={dangerous_classNamePopover}
        noHorizontalPadding={noHorizontalPadding}
      >
        {children}
      </Popover>
    </>
  );
};
